import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Privacy() {
  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="privacy-page">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Legal
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl md:text-5xl text-white mb-6">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-[#999999]">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={staggerContainer}
            className="prose prose-invert max-w-none"
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">1. Introduction</h2>
              <p className="text-[#999999] leading-relaxed">
                The Signal ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and protect your information when you use our website 
                and services.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">2. Data Controller</h2>
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 text-[#999999]">
                <p><strong className="text-white">Business Type:</strong> Eenmanszaak</p>
                <p><strong className="text-white">KVK Number:</strong> 42033086</p>
                <p><strong className="text-white">Vestigingsnummer:</strong> 000065359429</p>
                <p><strong className="text-white">Address:</strong> Gasthuisstraat 27, 5931NT Tegelen, Netherlands</p>
                <p><strong className="text-white">Email:</strong> contact@thesignalcore.com</p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">3. Information We Collect</h2>
              <p className="text-[#999999] leading-relaxed mb-4">We collect the following types of information:</p>
              <ul className="text-[#999999] space-y-2 list-disc list-inside">
                <li><strong className="text-white">Account Information:</strong> Email address, name, and password when you create an account</li>
                <li><strong className="text-white">Payment Information:</strong> Processed securely by Shopify - we do not store payment card details</li>
                <li><strong className="text-white">Communication Data:</strong> Messages sent through our contact form or chat support</li>
                <li><strong className="text-white">Usage Data:</strong> How you interact with our website (pages visited, time spent)</li>
                <li><strong className="text-white">Newsletter:</strong> Email address if you subscribe to our newsletter</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">4. How We Use Your Information</h2>
              <p className="text-[#999999] leading-relaxed mb-4">We use your information to:</p>
              <ul className="text-[#999999] space-y-2 list-disc list-inside">
                <li>Provide access to purchased digital products</li>
                <li>Process payments and manage subscriptions</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send important updates about your account or purchases</li>
                <li>Send marketing communications (only if you've opted in)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">5. Legal Basis for Processing (GDPR)</h2>
              <p className="text-[#999999] leading-relaxed mb-4">Under the General Data Protection Regulation (GDPR), we process your data based on:</p>
              <ul className="text-[#999999] space-y-2 list-disc list-inside">
                <li><strong className="text-white">Contract:</strong> To fulfill our agreement when you purchase our products</li>
                <li><strong className="text-white">Consent:</strong> For marketing communications and newsletters</li>
                <li><strong className="text-white">Legitimate Interest:</strong> To improve our services and prevent fraud</li>
                <li><strong className="text-white">Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">6. Data Sharing</h2>
              <p className="text-[#999999] leading-relaxed mb-4">We may share your data with:</p>
              <ul className="text-[#999999] space-y-2 list-disc list-inside">
                <li><strong className="text-white">Shopify:</strong> For payment processing and order management</li>
                <li><strong className="text-white">Email Service Providers:</strong> To send transactional and marketing emails</li>
                <li><strong className="text-white">Analytics Tools:</strong> To understand website usage (anonymized data)</li>
              </ul>
              <p className="text-[#999999] leading-relaxed mt-4">
                We do not sell your personal data to third parties.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">7. Data Retention</h2>
              <p className="text-[#999999] leading-relaxed">
                We retain your personal data for as long as necessary to provide our services and fulfill the purposes 
                described in this policy. Account data is retained while your account is active. After account deletion, 
                we may retain certain data for legal compliance (e.g., transaction records for tax purposes).
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">8. Your Rights (GDPR)</h2>
              <p className="text-[#999999] leading-relaxed mb-4">Under GDPR, you have the right to:</p>
              <ul className="text-[#999999] space-y-2 list-disc list-inside">
                <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-white">Rectification:</strong> Correct inaccurate data</li>
                <li><strong className="text-white">Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong className="text-white">Restriction:</strong> Limit how we process your data</li>
                <li><strong className="text-white">Portability:</strong> Receive your data in a portable format</li>
                <li><strong className="text-white">Object:</strong> Object to processing based on legitimate interest</li>
                <li><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent</li>
              </ul>
              <p className="text-[#999999] leading-relaxed mt-4">
                To exercise these rights, contact us at contact@thesignalcore.com.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">9. Cookies</h2>
              <p className="text-[#999999] leading-relaxed">
                We use essential cookies to enable core website functionality. We may also use analytics cookies 
                to understand how visitors use our site. You can manage cookie preferences through your browser settings.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">10. Data Security</h2>
              <p className="text-[#999999] leading-relaxed">
                We implement appropriate security measures to protect your data, including encryption for data 
                transmission, secure password hashing, and restricted access to personal information.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">11. International Transfers</h2>
              <p className="text-[#999999] leading-relaxed">
                Your data may be transferred to and processed in countries outside the European Economic Area (EEA). 
                When this occurs, we ensure appropriate safeguards are in place to protect your data in compliance with GDPR.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">12. Changes to This Policy</h2>
              <p className="text-[#999999] leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of significant changes 
                by posting a notice on our website or sending you an email.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">13. Contact & Complaints</h2>
              <p className="text-[#999999] leading-relaxed mb-4">
                For questions about this privacy policy or to exercise your rights, contact us at:{' '}
                <a href="mailto:contact@thesignalcore.com" className="text-[#D4AF37] hover:underline">
                  contact@thesignalcore.com
                </a>
              </p>
              <p className="text-[#999999] leading-relaxed">
                If you believe we have not handled your data correctly, you have the right to lodge a complaint 
                with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
